import React, { useState } from 'react';
import {
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Divider,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CustomTableHead, CustomTableHeadCell, CustomTableRow } from '../../components/@extended/CustomTable';
import { Edit, Check, Delete, Close, SyncProblemOutlined } from '@mui/icons-material';
import { useRequestVerificationMutation, useUpdateEventAmendmentMutation, useUpdateEventPayoutAmendmentMutation } from '../../store/slices/apiSlice';
import { useDispatch } from 'react-redux';
import { openMessage } from '../../store/slices/showMessageSlice';

const EventsSection = ({ protocolNumber, siteEntityId, events, onClose, isEditable = false }) => {
  const theme = useTheme();
  const [editableRowId, setEditableRowId] = useState(null);
  const [editData, setEditData] = useState({});
  const [editingSubjectPayment, setEditingSubjectPayment] = useState(null);
  const [subjectPaymentValues, setSubjectPaymentValues] = useState({});
  const dispatch = useDispatch();

  const [updateEventPayoutAmendment, { isLoading }] = useUpdateEventPayoutAmendmentMutation();
  const [requestVerification, { isLoading: verificationLoading }] = useRequestVerificationMutation();
  const [updateEventAmendment, { isLoading: isAmendmentLoading }] = useUpdateEventAmendmentMutation();

  // Handle edit button click for event payout row
  const handleEdit = (payout) => {
    setEditableRowId(payout.id);
    setEditData({
      amount: payout.amount,
    });
  };

  // Handle save for event payout row
  const handleSave = async (payout, event) => {
    // Create updated payout object with edited data

    const result = await updateEventPayoutAmendment({
      protocolNumber,
      siteEntityId,
      eventId: event.id,
      payoutId: payout.id,
      body: {
        amount: editData.amount,
      },
    });
    if (result.data) {
      dispatch(
        openMessage({
          messageSeverity: 'success',
          message: result.data.message || 'Payout updated successfully!',
        })
      );
    } else {
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: result.error.data.message || 'Failed to update payout!',
        })
      );
    }
    onClose();
    // Reset editing state
    setEditableRowId(null);
    setEditData({});
  };

  // Handle cancel for event payout row
  const handleCancel = () => {
    setEditableRowId(null);
    setEditData({});
  };

  // Handle input change for editable row
  const handleInputChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle edit for subject payment
  const handleEditSubjectPayment = (eventId, amount, currency) => {
    setEditingSubjectPayment(eventId);
    setSubjectPaymentValues({
      amount,
      currency,
    });
  };

  // Handle save for subject payment
  const handleSaveSubjectPayment = async (eventId) => {
    console.log('Saving subject payment for event:', eventId, subjectPaymentValues);

    const result = await updateEventAmendment({
      protocolNumber,
      siteEntityId,
      eventId: eventId,
      body: {
        subjectPaymentAmount: Number(subjectPaymentValues.amount),
      },
    });
    if (result.data) {
      dispatch(
        openMessage({
          messageSeverity: 'success',
          message: result.data.message || '!',
        })
      );
    } else {
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: result.error.data.message || 'Document downloaded successfully!',
        })
      );
    }

    onClose();
    setEditingSubjectPayment(null);
    setSubjectPaymentValues({});
  };

  const handleRequestVerification = async (entity) => {
    try {
      const res = await requestVerification(entity?.id).unwrap();
      if (res?.success) {
        dispatch(
          openMessage({
            messageSeverity: 'success',
            message: `Requested verification for ${entity?.email}`,
          })
        );
      }
    } catch (error) {
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: `Failed to request verification for ${entity?.email}`,
        })
      );
    }
  };

  // Handle cancel for subject payment
  const handleCancelSubjectPayment = () => {
    setEditingSubjectPayment(null);
    setSubjectPaymentValues({});
  };

  // Helper function to get recipient name
  const getRecipientName = (payout) => {
    return payout.targetEntity.addditionalInfo ? JSON.parse(payout.targetEntity.addditionalInfo).name : 'N/A';
  };

  return (
    <Box sx={{ width: '100%' }}>
      {events.map((event) => (
        <Box key={event.id} sx={{ mb: 4 }}>
          {/* Event Details Section */}
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {event.eventName}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
            <Typography variant="body2">
              <strong>Event Key:</strong> {event.eventKey}
            </Typography>
            <Typography variant="body2">
              <strong>Type:</strong> {event.type}
            </Typography>
            <Typography variant="body2">
              <strong>Approval Needed:</strong> {event.approvalNeeded ? 'Yes' : 'No'}
            </Typography>

            {/* Subject Payment with Edit Functionality */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                '&:hover .edit-icon': {
                  opacity: 1,
                },
              }}
            >
              {editingSubjectPayment === event.id ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Subject Payment:</strong>
                  </Typography>
                  <TextField
                    size="small"
                    value={subjectPaymentValues.amount}
                    onChange={(e) => setSubjectPaymentValues((prev) => ({ ...prev, amount: e.target.value }))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    sx={{ width: '100px' }}
                  />
                  {isAmendmentLoading ? (
                    <CircularProgress size={15} />
                  ) : (
                    <>
                      <IconButton size="small" color="success" onClick={() => handleSaveSubjectPayment(event.id)}>
                        <Check fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={handleCancelSubjectPayment}>
                        <Close fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              ) : (
                <>
                  <Typography variant="body2">
                    <strong>Subject Payment:</strong> {event.subjectPaymentAmount}{' '}
                    {event.subjectPaymentCurrency.toUpperCase()}
                  </Typography>
                 {isEditable && <IconButton
                    className="edit-icon"
                    size="small"
                    sx={{
                      ml: 1,
                      opacity: 0,
                      transition: 'opacity 0.2s',
                    }}
                    onClick={() =>
                      handleEditSubjectPayment(event.id, event.subjectPaymentAmount, event.subjectPaymentCurrency)
                    }
                  >
                    <Edit fontSize="small" />
                  </IconButton>}
                </>
              )}
            </Box>
          </Box>

          {/* Event Payout Table */}
          {event.eventPayoutDetails.length > 0 && (
            <TableContainer>
              <Table size="medium">
                <CustomTableHead>
                  <TableRow>
                    <CustomTableHeadCell>
                      <strong>Recipient</strong>
                    </CustomTableHeadCell>
                    <CustomTableHeadCell>
                      <strong>Email</strong>
                    </CustomTableHeadCell>
                    <CustomTableHeadCell>
                      <strong>Account Status</strong>
                    </CustomTableHeadCell>
                    <CustomTableHeadCell>
                      <strong>Amount</strong>
                    </CustomTableHeadCell>
                    <CustomTableHeadCell>
                      <strong>Currency</strong>
                    </CustomTableHeadCell>
                    {isEditable && <CustomTableHeadCell></CustomTableHeadCell>}
                  </TableRow>
                </CustomTableHead>
                <TableBody>
                  {event.eventPayoutDetails.map((payout) => (
                    <CustomTableRow key={payout.id}>
                      <TableCell>
                        <Tooltip title={getRecipientName(payout)}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: '150px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {getRecipientName(payout)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={payout.targetEntity.email}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: '180px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {payout.targetEntity.email}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          color:
                            payout.targetEntity?.accountStatus === 'Pending'
                              ? theme.palette.warning.main
                              : theme.palette.success.main,
                          fontWeight: '700',
                        }}
                      >
                        {payout.targetEntity.accountStatus}
                        {payout.targetEntity?.accountStatus === 'Pending' && (
                          <Tooltip
                            title={
                              <Box sx={{ p: 1 }}>
                                <Typography variant="body2">Onboarding/KYC pending: Request verification</Typography>
                              </Box>
                            }
                            arrow
                          >
                            <IconButton
                              aria-label="close"
                              onClick={() => handleRequestVerification(payout?.targetEntity)}
                              disabled={verificationLoading}
                              sx={{
                                color: (theme) => theme.palette.warning.main,
                              }}
                            >
                              <SyncProblemOutlined />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {editableRowId === payout.id ? (
                          <TextField
                            size="small"
                            value={editData.amount}
                            sx={{ width: 120 }}
                            onChange={(e) => handleInputChange('amount', e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                          />
                        ) : (
                          `$ ${payout.amount}`
                        )}
                      </TableCell>
                      <TableCell>{payout.currency.toUpperCase()}</TableCell>
                      {isEditable && (
                        <TableCell align="center">
                          {editableRowId === payout.id ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              {isLoading ? (
                                <CircularProgress size={15} />
                              ) : (
                                <>
                                  <IconButton color="success" onClick={() => handleSave(payout, event)} size="small">
                                    <Check fontSize="small" />
                                  </IconButton>
                                  <IconButton color="error" onClick={handleCancel} size="small">
                                    <Close fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </Box>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(payout)}
                              disabled={isLoading || isAmendmentLoading || editableRowId !== null}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                    </CustomTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box sx={{ pt: 2 }}>
            <Divider />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default EventsSection;
